import { useEffect, useRef, useState } from "react";
import { ActionIcon, Group, Text, Box } from "@mantine/core";
import type { AudioRef, AccessSession, TrackMarker } from "@/content/types";
import { resolveMedia } from "@/lib/media";
import { formatDuration } from "@/lib/format";
import { Waveform, type PeaksData } from "./Waveform";

interface Props {
  audio: AudioRef;
  session?: AccessSession | null;
  /**
   * Defaults to `audio.markers` — pass explicitly only if a render site
   * wants to suppress the audio's own markers (e.g. show a clean waveform).
   */
  markers?: TrackMarker[];
  label?: string;
}

interface PeaksJSON {
  version?: number;
  duration: number;
  peaks: PeaksData;
}

/**
 * Custom audio player: hidden <audio> element + Waveform component + transport.
 * Peaks are fetched from audio.peaksRef when present (see generate-peaks script);
 * otherwise the waveform renders a baseline and seek-on-click still works.
 */
export function AudioPlayer({ audio, session = null, markers = audio.markers, label }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [peaks, setPeaks] = useState<PeaksData | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(audio.durationSec);
  const [error, setError] = useState<string | null>(null);

  // Resolve the audio source (signed URL for private refs, base URL for public).
  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setError(null);
    resolveMedia(audio.ref, session)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "unknown");
      });
    return () => {
      cancelled = true;
    };
  }, [audio.ref, session]);

  // Resolve + fetch peaks if a peaksRef is provided.
  useEffect(() => {
    let cancelled = false;
    if (audio.peaks && audio.peaks.length > 0) {
      setPeaks(audio.peaks);
      return;
    }
    if (!audio.peaksRef) {
      setPeaks(null);
      return;
    }
    resolveMedia(audio.peaksRef, session)
      .then((url) => fetch(url))
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`peaks_${res.status}`))))
      .then((data: PeaksJSON) => {
        if (cancelled) return;
        setPeaks(data.peaks ?? null);
        if (typeof data.duration === "number" && data.duration > 0) {
          setDuration(data.duration);
        }
      })
      .catch(() => {
        if (!cancelled) setPeaks(null);
      });
    return () => {
      cancelled = true;
    };
  }, [audio.peaksRef, audio.peaks, session]);

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  }

  function seek(sec: number) {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = sec;
    setCurrentTime(sec);
  }

  if (error) {
    return (
      <Text size="sm" c="red">
        Audio unavailable ({error}).
      </Text>
    );
  }

  return (
    <Box>
      {label && (
        <Text size="xs" c="dimmed" mb={4}>
          {label}
        </Text>
      )}
      <audio
        ref={audioRef}
        src={src ?? undefined}
        preload="metadata"
        onLoadedMetadata={(e) => {
          if (!audio.peaksRef && !audio.peaks) {
            // No pre-rendered duration; read it from the audio element.
            setDuration(e.currentTarget.duration || audio.durationSec);
          }
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        style={{ display: "none" }}
      />
      <Waveform
        peaks={peaks}
        duration={duration}
        currentTime={currentTime}
        markers={markers}
        onSeek={seek}
      />
      <Group justify="space-between" mt="xs">
        <Group gap="xs">
          <ActionIcon
            size="lg"
            radius="xl"
            variant="filled"
            color="indigo"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <PauseGlyph /> : <PlayGlyph />}
          </ActionIcon>
          <Text size="xs" c="dimmed" ff="'JetBrains Mono', monospace">
            {formatDuration(currentTime)} / {formatDuration(duration)}
          </Text>
        </Group>
      </Group>
    </Box>
  );
}

function PlayGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <path d="M3 1.5v11l9-5.5z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
      <rect x="3" y="2" width="3" height="10" />
      <rect x="8" y="2" width="3" height="10" />
    </svg>
  );
}
